import React, { useState, useMemo } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    makeStyles,
    MenuItem,
    Paper,
    Tab,
    Tabs,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import ErrorIcon from '@material-ui/icons/Error'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { toast } from 'react-toastify'
import { DropFiles } from '../../components/DropFiles/DropFiles'
import { ContractsTable } from './ContractsTable'
import { ContractViewModal } from './ContractViewModal'
import {
    ContractTypeChoiceDialog,
    ContractUploadChoice,
} from './ContractTypeChoiceDialog'
import { ContractStatusChip } from './ContractStatusChip'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    FilterChipItem,
} from './CatalogFilterChrome'
import { ContractAlertsPanel } from './ContractAlertsPanel'
import { ContractAlertEmailSettingsDialog } from './ContractAlertEmailSettingsDialog'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import { QuickVendorData } from './QuickAddVendorModal'
import {
    updateContractRequest,
    useCompanyContracts,
} from '../../hooks/useCompanyContracts'
import { useContractAlerts } from '../../hooks/useContractAlerts'
import { useCompany } from '../../hooks/useCompany'
import { useUser } from '../../hooks/useUser'
import { useOrganizationCertificateTypes } from '../../hooks/useOrganizationCertificateTypes'
import { useOrganizationCertificateTypeCoverage } from '../../hooks/useOrganizationCertificateTypeCoverage'
import { useOrganizationContractTypes } from '../../hooks/useOrganizationContractTypes'
import { useOrganizationContractTypeCoverage } from '../../hooks/useOrganizationContractTypeCoverage'
import {
    ApartmentContract,
    ApartmentContractType,
    ContractLifecycleStatus,
} from '../../models/Company/types'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
    },
    searchField: {
        minWidth: 220,
        flex: '1 1 200px',
        maxWidth: 320,
    },
    filterField: {
        minWidth: 150,
    },
    analyticsContainer: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
        width: '100%',
        minWidth: 0,
    },
    analyticsCard: {
        flex: '1 1 0',
        minWidth: 0,
        padding: theme.spacing(1.5, 1.25),
        borderRadius: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
            boxShadow: theme.shadows[3],
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1, 0.75),
            gap: theme.spacing(0.75),
        },
    },
    analyticsIconContainer: {
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            width: 28,
            height: 28,
        },
    },
    analyticsIcon: {
        fontSize: 22,
        [theme.breakpoints.down('sm')]: {
            fontSize: 16,
        },
    },
    analyticsContent: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flex: 1,
    },
    analyticsValue: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            fontSize: 18,
        },
    },
    analyticsLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        fontWeight: 500,
        lineHeight: 1.25,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        [theme.breakpoints.down('sm')]: {
            fontSize: 10,
        },
    },
    totalCard: {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    totalIconContainer: {
        backgroundColor: theme.palette.primary.light + '30',
    },
    totalIcon: {
        color: theme.palette.primary.main,
    },
    activeCard: {
        borderLeft: '4px solid #22c55e',
    },
    activeIconContainer: {
        backgroundColor: '#dcfce7',
    },
    activeIcon: {
        color: '#16a34a',
    },
    endingSoonCard: {
        borderLeft: '4px solid #f59e0b',
    },
    endingSoonIconContainer: {
        backgroundColor: '#fef3c7',
    },
    endingSoonIcon: {
        color: '#d97706',
    },
    expiredCard: {
        borderLeft: '4px solid #ef4444',
    },
    expiredIconContainer: {
        backgroundColor: '#fee2e2',
    },
    expiredIcon: {
        color: '#dc2626',
    },
    autoRenewCard: {
        borderLeft: '4px solid #f59e0b',
        cursor: 'pointer',
    },
    autoRenewIconContainer: {
        backgroundColor: '#fef3c7',
    },
    autoRenewIcon: {
        color: '#d97706',
    },
    noDatesCard: {
        borderLeft: '4px solid #6b7280',
    },
    noDatesIconContainer: {
        backgroundColor: '#f3f4f6',
    },
    noDatesIcon: {
        color: '#6b7280',
    },
    clickableCard: {
        cursor: 'pointer',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
    },
    selectedCard: {
        boxShadow: '0 0 0 2px #2563eb',
    },
    requiredPanel: {
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    requiredPanelMissing: {
        borderColor: '#f59e0b',
        backgroundColor: '#fffbeb',
    },
    requiredPanelComplete: {
        borderColor: '#86efac',
        backgroundColor: '#f0fdf4',
    },
    alertRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(0.75, 1),
        borderRadius: theme.spacing(0.5),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))

// Allowed file types for DropFiles
const allowedFileTypes = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        '.docx',
    ],
    'text/plain': ['.txt'],
}

const CONTRACT_CATEGORIES: {
    value: ApartmentContractType
    label: string
}[] = [
    { value: ApartmentContractType.TURN, label: 'Turn Vendors' },
    { value: ApartmentContractType.GENERAL_VENDOR, label: 'General Vendor' },
]

interface ContractsProps {
    /** Certificates is its own manager tab. Contracts keeps vendor categories. */
    variant?: 'contracts' | 'certificates'
}

export const Contracts: React.FC<ContractsProps> = ({
    variant = 'contracts',
}) => {
    const classes = useStyles()
    const {
        contracts,
        isLoadingContracts,
        uploadContracts,
        processContract,
        updateContract,
        deleteContract,
        isUploading,
        isUpdating,
        isDeleting,
        processingIds,
    } = useCompanyContracts()

    const { alerts: allAlerts } = useContractAlerts()
    const isCertificates = variant === 'certificates'
    const { workspaceUser, organization: userOrganization } = useUser()
    const organizationId =
        workspaceUser?.active_workspace?.organization ??
        userOrganization ??
        null
    const activePropertyId = workspaceUser?.active_workspace?.id ?? null
    const { contractTypes } = useOrganizationContractTypes({
        organization: isCertificates ? null : organizationId,
        includeInactive: false,
    })
    const { certificateTypes } = useOrganizationCertificateTypes({
        organization: isCertificates ? organizationId : null,
        includeInactive: false,
    })
    const catalogTypes = isCertificates ? certificateTypes : contractTypes
    const {
        coverage: contractTypeCoverage,
        isLoading: isLoadingContractCoverage,
    } = useOrganizationContractTypeCoverage({
        organization: organizationId,
        includeInactive: false,
        enabled:
            !isCertificates &&
            organizationId != null &&
            activePropertyId != null,
    })
    const {
        coverage: certificateTypeCoverage,
        isLoading: isLoadingCertificateCoverage,
    } = useOrganizationCertificateTypeCoverage({
        organization: organizationId,
        includeInactive: false,
        enabled:
            isCertificates &&
            organizationId != null &&
            activePropertyId != null,
    })
    const activeTypeCoverage = isCertificates
        ? certificateTypeCoverage
        : contractTypeCoverage
    const isLoadingCoverage = isCertificates
        ? isLoadingCertificateCoverage
        : isLoadingContractCoverage
    const requiredContractStatus = useMemo(
        () =>
            activeTypeCoverage
                .map((group) => {
                    const propertyCoverage = group.properties.find(
                        (property) => property.id === activePropertyId,
                    )
                    return {
                        contractType: group.contract_type,
                        isRequired: propertyCoverage?.is_required ?? false,
                        hasDocument: propertyCoverage?.has_document ?? false,
                    }
                })
                .filter((status) => status.isRequired),
        [activePropertyId, activeTypeCoverage],
    )
    const missingRequiredContractTypes = requiredContractStatus.filter(
        (status) => !status.hasDocument,
    )

    const { vendorList, createVendor } = useCompany({
        getVendorList: true,
        cleanUp: true,
    })

    // Create vendor state
    const [isCreatingVendor, setIsCreatingVendor] = useState(false)

    // Search state
    const [searchText, setSearchText] = useState('')
    const [
        contractCategory,
        setContractCategory,
    ] = useState<ApartmentContractType>(ApartmentContractType.GENERAL_VENDOR)
    const alerts = allAlerts.filter((alert) => {
        const type =
            alert.contract.contract_type ?? ApartmentContractType.GENERAL_VENDOR
        return isCertificates
            ? type === ApartmentContractType.CERTIFICATE
            : type === contractCategory
    })
    const [statusFilter, setStatusFilter] = useState<
        ContractLifecycleStatus | 'all'
    >('all')
    const [catalogTypeFilter, setCatalogTypeFilter] = useState<
        'all' | 'untyped' | number
    >('all')
    const [vendorFilter, setVendorFilter] = useState<
        'all' | 'unassigned' | number
    >('all')
    const [autoRenewFilter, setAutoRenewFilter] = useState<
        'all' | 'yes' | 'no'
    >('all')
    const [showCompleteRequired, setShowCompleteRequired] = useState(false)
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [emailSettingsOpen, setEmailSettingsOpen] = useState(false)

    const [contractTypeDialogOpen, setContractTypeDialogOpen] = useState(false)
    const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([])

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // View modal state
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [viewStartsEditing, setViewStartsEditing] = useState(false)
    const [
        selectedContract,
        setSelectedContract,
    ] = useState<ApartmentContract | null>(null)
    const viewedContract = useMemo(() => {
        if (!selectedContract) return null
        return (
            contracts.find((item) => item.id === selectedContract.id) ??
            selectedContract
        )
    }, [contracts, selectedContract])

    // Delete confirmation state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [
        contractToDelete,
        setContractToDelete,
    ] = useState<ApartmentContract | null>(null)

    const scopedContracts = useMemo(
        () =>
            contracts.filter((contract) => {
                const type =
                    contract.contract_type ??
                    ApartmentContractType.GENERAL_VENDOR
                return isCertificates
                    ? type === ApartmentContractType.CERTIFICATE
                    : type === contractCategory
            }),
        [contracts, isCertificates, contractCategory],
    )

    const scopedAnalytics = useMemo(
        () => ({
            total: scopedContracts.length,
            active: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'active',
            ).length,
            ending_soon: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'ending_soon',
            ).length,
            auto_renew_notice: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'auto_renew_notice',
            ).length,
            expired: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'expired',
            ).length,
            no_dates: scopedContracts.filter(
                (contract) => contract.lifecycle_status === 'no_dates',
            ).length,
        }),
        [scopedContracts],
    )

    const vendorOptions = useMemo(() => {
        const names = new Map<number, string>()
        scopedContracts.forEach((contract) => {
            if (contract.vendor?.id) {
                names.set(contract.vendor.id, contract.vendor.name)
            }
        })
        return Array.from(names.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [scopedContracts])

    // Filter contracts by category, status, and search text
    const filteredContracts = useMemo(() => {
        let list = scopedContracts
        if (statusFilter !== 'all') {
            list = list.filter(
                (contract) => contract.lifecycle_status === statusFilter,
            )
        }
        if (catalogTypeFilter === 'untyped') {
            list = list.filter((contract) =>
                isCertificates
                    ? !contract.organization_certificate_type
                    : !contract.organization_contract_type,
            )
        } else if (catalogTypeFilter !== 'all') {
            list = list.filter((contract) =>
                isCertificates
                    ? contract.organization_certificate_type ===
                      catalogTypeFilter
                    : contract.organization_contract_type === catalogTypeFilter,
            )
        }
        if (vendorFilter === 'unassigned') {
            list = list.filter((contract) => !contract.vendor?.id)
        } else if (vendorFilter !== 'all') {
            list = list.filter(
                (contract) => contract.vendor?.id === vendorFilter,
            )
        }
        if (autoRenewFilter !== 'all') {
            list = list.filter((contract) =>
                autoRenewFilter === 'yes'
                    ? contract.auto_renew
                    : !contract.auto_renew,
            )
        }
        if (searchText.trim()) {
            const searchLower = searchText.toLowerCase()
            list = list.filter((contract) => {
                const catalogName = isCertificates
                    ? contract.organization_certificate_type_detail?.name
                    : contract.organization_contract_type_detail?.name
                return (
                    contract.name.toLowerCase().includes(searchLower) ||
                    (contract.ai_summary || '')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (contract.vendor?.name || '')
                        .toLowerCase()
                        .includes(searchLower) ||
                    (catalogName || '').toLowerCase().includes(searchLower)
                )
            })
        }
        return list
    }, [
        scopedContracts,
        searchText,
        statusFilter,
        catalogTypeFilter,
        vendorFilter,
        autoRenewFilter,
        isCertificates,
    ])

    const hasActiveLibraryFilters =
        searchText.trim() !== '' ||
        statusFilter !== 'all' ||
        catalogTypeFilter !== 'all' ||
        vendorFilter !== 'all' ||
        autoRenewFilter !== 'all'

    const clearDrawerFilters = () => {
        setStatusFilter('all')
        setCatalogTypeFilter('all')
        setVendorFilter('all')
        setAutoRenewFilter('all')
        setPage(0)
    }

    const libraryFilterChips = useMemo<FilterChipItem[]>(() => {
        const chips: FilterChipItem[] = []
        if (catalogTypeFilter === 'untyped') {
            chips.push({
                id: 'type',
                label: 'Type: Untyped',
                onDelete: () => setCatalogTypeFilter('all'),
            })
        } else if (catalogTypeFilter !== 'all') {
            const typeName =
                catalogTypes.find((type) => type.id === catalogTypeFilter)
                    ?.name || 'Type'
            chips.push({
                id: 'type',
                label: `Type: ${typeName}`,
                onDelete: () => setCatalogTypeFilter('all'),
            })
        }
        if (statusFilter !== 'all') {
            const labels: Record<ContractLifecycleStatus, string> = {
                active: 'Active',
                ending_soon: 'Ending soon',
                auto_renew_notice: 'Auto-renew notice',
                expired: 'Expired',
                no_dates: 'No dates',
            }
            chips.push({
                id: 'status',
                label: `Status: ${labels[statusFilter]}`,
                onDelete: () => setStatusFilter('all'),
            })
        }
        if (vendorFilter === 'unassigned') {
            chips.push({
                id: 'vendor',
                label: 'Vendor: Unassigned',
                onDelete: () => setVendorFilter('all'),
            })
        } else if (vendorFilter !== 'all') {
            const vendorName =
                vendorOptions.find((vendor) => vendor.id === vendorFilter)
                    ?.name || 'Vendor'
            chips.push({
                id: 'vendor',
                label: `Vendor: ${vendorName}`,
                onDelete: () => setVendorFilter('all'),
            })
        }
        if (autoRenewFilter !== 'all') {
            chips.push({
                id: 'autorenew',
                label:
                    autoRenewFilter === 'yes'
                        ? 'Auto-renew: Yes'
                        : 'Auto-renew: No',
                onDelete: () => setAutoRenewFilter('all'),
            })
        }
        return chips
    }, [
        catalogTypeFilter,
        catalogTypes,
        statusFilter,
        vendorFilter,
        vendorOptions,
        autoRenewFilter,
    ])

    // Paginate filtered contracts
    const paginatedContracts = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredContracts.slice(start, end)
    }, [filteredContracts, page, rowsPerPage])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleFilesDropped = (files: File[]) => {
        if (files.length === 0) return
        setPendingUploadFiles(files)
        setContractTypeDialogOpen(true)
    }

    const handleCloseContractTypeDialog = () => {
        setContractTypeDialogOpen(false)
        setPendingUploadFiles([])
    }

    const handleContractTypeChosen = async (choice: ContractUploadChoice) => {
        setContractTypeDialogOpen(false)
        const files = pendingUploadFiles
        setPendingUploadFiles([])
        if (files.length === 0) return

        const results = await uploadContracts(
            files,
            undefined,
            choice.category,
            isCertificates ? null : choice.organizationContractTypeId,
            isCertificates ? choice.organizationContractTypeId : null,
        )

        results.forEach((result) => {
            if (result.status === 'fulfilled') {
                const contract = result.value.data
                processContract.mutate(contract.id)
            }
        })
    }

    const handleView = (contract: ApartmentContract) => {
        setSelectedContract(contract)
        setViewStartsEditing(false)
        setViewModalOpen(true)
    }

    const handleCloseViewModal = () => {
        setViewModalOpen(false)
        setSelectedContract(null)
    }

    const handleDeleteClick = (id: number) => {
        const contract = contracts.find((c) => c.id === id)
        if (contract) {
            setContractToDelete(contract)
            setDeleteDialogOpen(true)
        }
    }

    const handleConfirmDelete = () => {
        if (contractToDelete) {
            deleteContract.mutate(contractToDelete.id, {
                onSuccess: () => {
                    setDeleteDialogOpen(false)
                    setContractToDelete(null)
                },
            })
        }
    }

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false)
        setContractToDelete(null)
    }

    const handleShare = async (contract: ApartmentContract) => {
        try {
            await navigator.clipboard.writeText(contract.file)
            toast.success('Contract link copied to clipboard.')
        } catch (error) {
            // Fallback for browsers that don't support clipboard API
            const textArea = window.document.createElement('textarea')
            textArea.value = contract.file
            window.document.body.appendChild(textArea)
            textArea.select()
            window.document.execCommand('copy')
            window.document.body.removeChild(textArea)
            toast.success('Contract link copied to clipboard.')
        }
    }

    const handleReanalyze = (id: number) => {
        processContract.mutate(id)
    }

    const handleEdit = (contract: ApartmentContract) => {
        setSelectedContract(contract)
        setViewStartsEditing(true)
        setViewModalOpen(true)
    }

    const handleSaveContract = (
        contractId: number,
        data: updateContractRequest,
    ) => updateContract.mutateAsync({ contractId, data })

    const handleCreateVendor = async (
        data: QuickVendorData,
    ): Promise<number> => {
        setIsCreatingVendor(true)
        try {
            const result = await createVendor({
                body: {
                    name: data.name,
                    email: data.email,
                    phone: data.phone,
                    address: data.address,
                    city: data.city,
                    state: data.state,
                    zip_code: data.zip_code,
                    in_house: data.in_house,
                    service_contracts: [],
                    services: [],
                    is_skillcat: false,
                },
            })
            toast.success(`Vendor "${data.name}" created successfully`)
            return result.data.vendor.id
        } catch (error: any) {
            if (error?.response?.data?.key !== 'INVALID_PHONE') {
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    'Failed to create vendor'
                toast.error(errorMessage)
            }
            throw error
        } finally {
            setIsCreatingVendor(false)
        }
    }

    if (isLoadingContracts) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography
                    variant="h5"
                    component="h2"
                    className={classes.title}
                >
                    {isCertificates ? 'Certificates' : 'Contracts'}
                </Typography>
            </Box>

            {/* Analytics Cards */}
            <Box className={classes.analyticsContainer}>
                <Paper
                    className={`${classes.analyticsCard} ${classes.totalCard} ${
                        classes.clickableCard
                    } ${statusFilter === 'all' ? classes.selectedCard : ''}`}
                    elevation={1}
                    onClick={() => setStatusFilter('all')}
                >
                    <Box
                        className={`${classes.analyticsIconContainer} ${classes.totalIconContainer}`}
                    >
                        <DescriptionIcon
                            className={`${classes.analyticsIcon} ${classes.totalIcon}`}
                        />
                    </Box>
                    <Box className={classes.analyticsContent}>
                        <Typography className={classes.analyticsValue}>
                            {scopedAnalytics.total}
                        </Typography>
                        <Typography className={classes.analyticsLabel}>
                            {isCertificates
                                ? 'Total Certificates'
                                : 'Total Contracts'}
                        </Typography>
                    </Box>
                </Paper>

                <Paper
                    className={`${classes.analyticsCard} ${
                        classes.activeCard
                    } ${classes.clickableCard} ${
                        statusFilter === 'active' ? classes.selectedCard : ''
                    }`}
                    elevation={1}
                    onClick={() =>
                        setStatusFilter(
                            statusFilter === 'active' ? 'all' : 'active',
                        )
                    }
                >
                    <Box
                        className={`${classes.analyticsIconContainer} ${classes.activeIconContainer}`}
                    >
                        <CheckCircleIcon
                            className={`${classes.analyticsIcon} ${classes.activeIcon}`}
                        />
                    </Box>
                    <Box className={classes.analyticsContent}>
                        <Typography className={classes.analyticsValue}>
                            {scopedAnalytics.active}
                        </Typography>
                        <Typography className={classes.analyticsLabel}>
                            Active
                        </Typography>
                    </Box>
                </Paper>

                <Paper
                    className={`${classes.analyticsCard} ${
                        classes.endingSoonCard
                    } ${classes.clickableCard} ${
                        statusFilter === 'ending_soon'
                            ? classes.selectedCard
                            : ''
                    }`}
                    elevation={1}
                    onClick={() =>
                        setStatusFilter(
                            statusFilter === 'ending_soon'
                                ? 'all'
                                : 'ending_soon',
                        )
                    }
                >
                    <Box
                        className={`${classes.analyticsIconContainer} ${classes.endingSoonIconContainer}`}
                    >
                        <WarningIcon
                            className={`${classes.analyticsIcon} ${classes.endingSoonIcon}`}
                        />
                    </Box>
                    <Box className={classes.analyticsContent}>
                        <Typography className={classes.analyticsValue}>
                            {scopedAnalytics.ending_soon}
                        </Typography>
                        <Typography className={classes.analyticsLabel}>
                            Ending Soon
                        </Typography>
                    </Box>
                </Paper>

                <Paper
                    className={`${classes.analyticsCard} ${
                        classes.autoRenewCard
                    } ${classes.clickableCard} ${
                        statusFilter === 'auto_renew_notice'
                            ? classes.selectedCard
                            : ''
                    }`}
                    elevation={1}
                    onClick={() =>
                        setStatusFilter(
                            statusFilter === 'auto_renew_notice'
                                ? 'all'
                                : 'auto_renew_notice',
                        )
                    }
                >
                    <Box
                        className={`${classes.analyticsIconContainer} ${classes.autoRenewIconContainer}`}
                    >
                        <AutorenewIcon
                            className={`${classes.analyticsIcon} ${classes.autoRenewIcon}`}
                        />
                    </Box>
                    <Box className={classes.analyticsContent}>
                        <Typography className={classes.analyticsValue}>
                            {scopedAnalytics.auto_renew_notice}
                        </Typography>
                        <Typography className={classes.analyticsLabel}>
                            Auto-Renew
                        </Typography>
                    </Box>
                </Paper>

                <Paper
                    className={`${classes.analyticsCard} ${
                        classes.expiredCard
                    } ${classes.clickableCard} ${
                        statusFilter === 'expired' ? classes.selectedCard : ''
                    }`}
                    elevation={1}
                    onClick={() =>
                        setStatusFilter(
                            statusFilter === 'expired' ? 'all' : 'expired',
                        )
                    }
                >
                    <Box
                        className={`${classes.analyticsIconContainer} ${classes.expiredIconContainer}`}
                    >
                        <ErrorIcon
                            className={`${classes.analyticsIcon} ${classes.expiredIcon}`}
                        />
                    </Box>
                    <Box className={classes.analyticsContent}>
                        <Typography className={classes.analyticsValue}>
                            {scopedAnalytics.expired}
                        </Typography>
                        <Typography className={classes.analyticsLabel}>
                            Expired
                        </Typography>
                    </Box>
                </Paper>

                <Paper
                    className={`${classes.analyticsCard} ${
                        classes.noDatesCard
                    } ${classes.clickableCard} ${
                        statusFilter === 'no_dates' ? classes.selectedCard : ''
                    }`}
                    elevation={1}
                    onClick={() =>
                        setStatusFilter(
                            statusFilter === 'no_dates' ? 'all' : 'no_dates',
                        )
                    }
                >
                    <Box
                        className={`${classes.analyticsIconContainer} ${classes.noDatesIconContainer}`}
                    >
                        <HelpOutlineIcon
                            className={`${classes.analyticsIcon} ${classes.noDatesIcon}`}
                        />
                    </Box>
                    <Box className={classes.analyticsContent}>
                        <Typography className={classes.analyticsValue}>
                            {scopedAnalytics.no_dates}
                        </Typography>
                        <Typography className={classes.analyticsLabel}>
                            No Dates
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            <ContractAlertsPanel
                alerts={alerts}
                itemNoun={isCertificates ? 'certificate' : 'contract'}
                storageKey={
                    isCertificates
                        ? 'property-certificate-alerts-closed'
                        : 'property-contract-alerts-closed'
                }
                onSelect={(contract) => {
                    setSelectedContract(contract)
                    setViewModalOpen(true)
                }}
                onOpenEmailSettings={() => setEmailSettingsOpen(true)}
            />
            <ContractAlertEmailSettingsDialog
                open={emailSettingsOpen}
                onClose={() => setEmailSettingsOpen(false)}
                organization={organizationId}
                apartment={activePropertyId}
                scopeLabel="This property only. It overrides the organization default."
            />

            {organizationId != null &&
                activePropertyId != null &&
                (isLoadingCoverage || requiredContractStatus.length > 0) && (
                    <Paper
                        elevation={0}
                        className={`${classes.requiredPanel} ${
                            missingRequiredContractTypes.length > 0
                                ? classes.requiredPanelMissing
                                : classes.requiredPanelComplete
                        }`}
                    >
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 600 }}
                                >
                                    {isCertificates
                                        ? 'Required certificates'
                                        : 'Required contracts'}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {isCertificates
                                        ? 'Certificate types this property is expected to have on file.'
                                        : 'Contract types this property is expected to have on file.'}
                                </Typography>
                            </Box>
                            {isLoadingCoverage ? (
                                <CircularProgress size={18} />
                            ) : (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    style={{ gap: 8 }}
                                >
                                    {requiredContractStatus.length >
                                        missingRequiredContractTypes.length && (
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                setShowCompleteRequired(
                                                    (open) => !open,
                                                )
                                            }
                                        >
                                            {showCompleteRequired
                                                ? 'Hide on file'
                                                : 'Show on file'}
                                        </Button>
                                    )}
                                    <Chip
                                        size="small"
                                        color={
                                            missingRequiredContractTypes.length >
                                            0
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        label={
                                            missingRequiredContractTypes.length >
                                            0
                                                ? `${missingRequiredContractTypes.length} missing`
                                                : 'Complete'
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        {!isLoadingCoverage &&
                            (missingRequiredContractTypes.length > 0 ||
                                showCompleteRequired) && (
                                <Box
                                    display="flex"
                                    flexWrap="wrap"
                                    style={{ gap: 8 }}
                                >
                                    {(showCompleteRequired
                                        ? requiredContractStatus
                                        : missingRequiredContractTypes
                                    ).map(({ contractType, hasDocument }) => (
                                        <Chip
                                            key={contractType.id}
                                            label={
                                                showCompleteRequired
                                                    ? `${contractType.name}${
                                                          hasDocument
                                                              ? ' · on file'
                                                              : ' · missing'
                                                      }`
                                                    : contractType.name
                                            }
                                            size="small"
                                            color={
                                                hasDocument
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                        />
                                    ))}
                                </Box>
                            )}
                    </Paper>
                )}

            <DropFiles
                onDrop={handleFilesDropped}
                displayString={
                    isUploading
                        ? 'Uploading...'
                        : isCertificates
                        ? 'Drag and drop certificate files here (PDF, DOCX, TXT)'
                        : 'Drag and drop your contract files here (PDF, DOCX, TXT)'
                }
                fileTypes={allowedFileTypes}
                multipleFiles={true}
            />

            {!isCertificates && (
                <Tabs
                    value={contractCategory}
                    onChange={(_event, value: ApartmentContractType) => {
                        setContractCategory(value)
                        setPage(0)
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    {CONTRACT_CATEGORIES.map((category) => (
                        <Tab
                            key={category.value}
                            value={category.value}
                            label={category.label}
                            style={{ textTransform: 'none', fontWeight: 500 }}
                        />
                    ))}
                </Tabs>
            )}

            <ContractTypeChoiceDialog
                open={contractTypeDialogOpen}
                onClose={handleCloseContractTypeDialog}
                fileCount={pendingUploadFiles.length}
                onChoose={handleContractTypeChosen}
                contractTypes={catalogTypes}
                title={
                    isCertificates ? 'Upload certificates' : 'Upload contracts'
                }
                catalogLabel={
                    isCertificates ? 'Certificate type' : 'Contract type'
                }
                lockedCategory={
                    isCertificates
                        ? ApartmentContractType.CERTIFICATE
                        : contractCategory
                }
            />

            <CatalogFilterBar
                searchPlaceholder={
                    isCertificates
                        ? 'Search certificates...'
                        : 'Search contracts...'
                }
                searchValue={searchText}
                onSearchChange={(value) => {
                    setSearchText(value)
                    setPage(0)
                }}
                onOpenFilters={() => setFilterDrawerOpen(true)}
                chips={libraryFilterChips}
                onClearAll={
                    libraryFilterChips.length > 0
                        ? clearDrawerFilters
                        : undefined
                }
                resultLabel={`${filteredContracts.length} ${
                    isCertificates ? 'certificate' : 'contract'
                }${filteredContracts.length !== 1 ? 's' : ''}`}
            />
            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title={
                    isCertificates ? 'Certificate filters' : 'Contract filters'
                }
                activeCount={libraryFilterChips.length}
                onClear={clearDrawerFilters}
            >
                <TextField
                    label={
                        isCertificates ? 'Certificate type' : 'Contract type'
                    }
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={catalogTypeFilter}
                    onChange={(e) => {
                        const value = e.target.value
                        setCatalogTypeFilter(
                            value === 'all' || value === 'untyped'
                                ? value
                                : Number(value),
                        )
                        setPage(0)
                    }}
                >
                    <MenuItem value="all">All types</MenuItem>
                    <MenuItem value="untyped">Untyped</MenuItem>
                    {catalogTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                            {type.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Status"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(
                            e.target.value as ContractLifecycleStatus | 'all',
                        )
                        setPage(0)
                    }}
                >
                    <MenuItem value="all">All statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="ending_soon">Ending soon</MenuItem>
                    <MenuItem value="auto_renew_notice">
                        Auto-renew notice
                    </MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="no_dates">No dates</MenuItem>
                </TextField>
                <TextField
                    label="Vendor"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={vendorFilter}
                    onChange={(e) => {
                        const value = e.target.value
                        setVendorFilter(
                            value === 'all' || value === 'unassigned'
                                ? value
                                : Number(value),
                        )
                        setPage(0)
                    }}
                >
                    <MenuItem value="all">All vendors</MenuItem>
                    <MenuItem value="unassigned">Unassigned</MenuItem>
                    {vendorOptions.map((vendor) => (
                        <MenuItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Auto-renew"
                    variant="outlined"
                    size="small"
                    select
                    fullWidth
                    value={autoRenewFilter}
                    onChange={(e) =>
                        setAutoRenewFilter(
                            e.target.value as 'all' | 'yes' | 'no',
                        )
                    }
                >
                    <MenuItem value="all">Any</MenuItem>
                    <MenuItem value="yes">Auto-renew</MenuItem>
                    <MenuItem value="no">No auto-renew</MenuItem>
                </TextField>
            </CatalogFilterDrawer>

            <ContractsTable
                contracts={paginatedContracts}
                onDelete={handleDeleteClick}
                onView={handleView}
                onShare={handleShare}
                onReanalyze={handleReanalyze}
                onEdit={handleEdit}
                processingIds={processingIds}
                // Pagination props
                totalCount={filteredContracts.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                emptyMessage={
                    isCertificates
                        ? 'No certificates found.'
                        : 'No contracts found.'
                }
            />

            {/* View Contract Modal */}
            <ContractViewModal
                isOpen={viewModalOpen}
                onClose={handleCloseViewModal}
                contract={viewedContract}
                initialEditing={viewStartsEditing}
                vendorList={vendorList}
                onSave={handleSaveContract}
                isSaving={isUpdating}
                onCreateVendor={handleCreateVendor}
                isCreatingVendor={isCreatingVendor}
            />

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                documentName={contractToDelete?.name || ''}
                isDeleting={isDeleting}
            />

        </Box>
    )
}
